import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Skeleton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Edit, Delete, Refresh, Visibility } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { AppDispatch, RootState } from '../../../store/store';
import {
  fetchRoleTemplates,
  createRoleTemplate,
  updateRoleTemplate,
  deleteRoleTemplate,
} from '../../../store/slices/superAdminSlice';
import { RoleTemplateDto, CreateRoleTemplate, UpdateRoleTemplate, RolePermissionItem } from '../../../types/superAdminTypes';

const DEFAULT_MODULES = ['Dashboard', 'Employees', 'Attendance', 'Leave', 'Shift', 'Location', 'Payroll', 'Reports', 'Settings'];

const createEmptyPermissions = (): Omit<RolePermissionItem, 'id'>[] =>
  DEFAULT_MODULES.map((module) => ({ module, canView: false, canCreate: false, canUpdate: false, canDelete: false }));

const getRoleNameError = (roleName: string) =>
  roleName.trim() ? '' : 'Role Name is required';

const RoleTemplatesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { roleTemplates, loading, error } = useSelector((state: RootState) => state.superAdmin);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleTemplateDto | null>(null);
  const [viewRole, setViewRole] = useState<RoleTemplateDto | null>(null);
  const [roleNameTouched, setRoleNameTouched] = useState(false);
  const [form, setForm] = useState<CreateRoleTemplate>({
    roleName: '',
    description: '',
    permissions: createEmptyPermissions(),
    isDefault: false,
  });

  const fetchData = useCallback(() => { dispatch(fetchRoleTemplates()); }, [dispatch]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpenCreate = () => {
    setEditingRole(null);
    setForm({ roleName: '', description: '', permissions: createEmptyPermissions(), isDefault: false });
    setRoleNameTouched(false);
    setOpenDialog(true);
  };

  const handleOpenEdit = (role: RoleTemplateDto) => {
    setEditingRole(role);
    setForm({ roleName: role.roleName, description: role.description || '', permissions: role.permissions.length > 0 ? role.permissions : createEmptyPermissions(), isDefault: role.isDefault });
    setRoleNameTouched(false);
    setOpenDialog(true);
  };

  const handlePermissionChange = (moduleIdx: number, field: keyof RolePermissionItem, value: boolean) => {
    const updated = [...form.permissions];
    updated[moduleIdx] = { ...updated[moduleIdx], [field]: value };
    setForm({ ...form, permissions: updated });
  };

  const handleSubmit = async () => {
    const roleNameError = getRoleNameError(form.roleName);
    if (roleNameError) {
      setRoleNameTouched(true);
      toast.error(roleNameError);
      return;
    }

    const payload = {
      ...form,
      roleName: form.roleName.trim(),
      description: (form.description || '').trim(),
    };

    try {
      if (editingRole) {
        const data: UpdateRoleTemplate = { ...payload, id: editingRole.id };
        await dispatch(updateRoleTemplate({ id: editingRole.id, data })).unwrap();
        toast.success('Role template updated');
      } else {
        await dispatch(createRoleTemplate(payload)).unwrap();
        toast.success('Role template created');
      }
      setOpenDialog(false);
      fetchData();
    } catch (err: any) {
      toast.error(err || 'Operation failed');
    }
  };

  const roleNameError = roleNameTouched ? getRoleNameError(form.roleName) : '';
  const isSubmitDisabled = loading || !!getRoleNameError(form.roleName);

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({ title: 'Delete Role Template?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#f44336', confirmButtonText: 'Delete' });
    if (result.isConfirmed) {
      try { await dispatch(deleteRoleTemplate(id)).unwrap(); toast.success('Deleted'); } catch (err: any) { toast.error(err || 'Failed'); }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>Role Templates</Typography>
          <Typography variant="body2" color="text.secondary">Default roles and permissions for tenants</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', whiteSpace: 'nowrap' }}>Add Role</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Role Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Default</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Permissions</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              ))
            ) : roleTemplates.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No role templates found</Typography></TableCell></TableRow>
            ) : (
              roleTemplates.map((role) => (
                <TableRow key={role.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{role.roleName}</Typography></TableCell>
                  <TableCell>{role.description || '—'}</TableCell>
                  <TableCell><Chip label={role.isDefault ? 'Yes' : 'No'} color={role.isDefault ? 'info' : 'default'} size="small" /></TableCell>
                  <TableCell>{role.permissions.length} modules</TableCell>
                  <TableCell>{new Date(role.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Permissions"><IconButton size="small" onClick={() => setViewRole(role)}><Visibility fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenEdit(role)}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(role.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Permissions Dialog */}
      <Dialog open={!!viewRole} onClose={() => setViewRole(null)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>Permissions — {viewRole?.roleName}</DialogTitle>
        <DialogContent>
          {viewRole && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Module</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>View</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Create</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Update</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewRole.permissions.map((p) => (
                    <TableRow key={p.module}>
                      <TableCell>{p.module}</TableCell>
                      <TableCell align="center"><Chip label={p.canView ? '✓' : '✗'} color={p.canView ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell align="center"><Chip label={p.canCreate ? '✓' : '✗'} color={p.canCreate ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell align="center"><Chip label={p.canUpdate ? '✓' : '✗'} color={p.canUpdate ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell align="center"><Chip label={p.canDelete ? '✓' : '✗'} color={p.canDelete ? 'success' : 'default'} size="small" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setViewRole(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
         <DialogTitle>
        {editingRole ? 'Edit Role Template' : 'Create Role Template'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Role Name"
                value={form.roleName}
                onChange={(e) => setForm({ ...form, roleName: e.target.value })}
                onBlur={() => setRoleNameTouched(true)}
                required
                error={!!roleNameError}
                helperText={roleNameError}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel control={<Switch checked={form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} />} label="Default Role" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Permissions</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Module</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>View</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Create</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Update</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Delete</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form.permissions.map((p, idx) => (
                      <TableRow key={p.module}>
                        <TableCell>{p.module}</TableCell>
                        <TableCell align="center"><Checkbox checked={p.canView} onChange={(e) => handlePermissionChange(idx, 'canView', e.target.checked)} size="small" /></TableCell>
                        <TableCell align="center"><Checkbox checked={p.canCreate} onChange={(e) => handlePermissionChange(idx, 'canCreate', e.target.checked)} size="small" /></TableCell>
                        <TableCell align="center"><Checkbox checked={p.canUpdate} onChange={(e) => handlePermissionChange(idx, 'canUpdate', e.target.checked)} size="small" /></TableCell>
                        <TableCell align="center"><Checkbox checked={p.canDelete} onChange={(e) => handlePermissionChange(idx, 'canDelete', e.target.checked)} size="small" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            {editingRole ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleTemplatesPage;
